import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Search, ListChecks, CircleUser, ShieldCheck, Star } from 'lucide-react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { useAuth } from '@/hooks/useAuth'
import { usePreferences } from '@/lib/preferences'
import { RELEASES, LATEST_RELEASE } from '@/lib/whatsNew'
import { formatDate } from '@/lib/format'

/**
 * First-login welcome (a short guided tour, role-aware) and the "What's new" dialog (E-UX).
 * Dismissal is a server-side preference, so the welcome shows once per person, not per browser.
 */
interface Step { icon: typeof Sparkles; title: string; body: string; link?: { to: string; label: string } }

function stepsFor(role: string | undefined): Step[] {
  const staff = role === 'HR_ADMIN' || role === 'HR_EXECUTIVE' || role === 'SUPER_ADMIN'
  const approver = staff || role === 'MANAGER'
  return [
    { icon: Sparkles, title: 'Welcome to BIGROI HRMS', body: 'A quick tour of where things are. It takes under a minute, and you can reopen it any time from the user menu.' },
    { icon: Search, title: 'Find anything', body: 'Use the search box at the top (or press Ctrl K) to jump to a page, a person or a record. Press ? for all keyboard shortcuts.' },
    ...(approver ? [{ icon: ListChecks, title: 'Your approvals in one place', body: 'Leave, claims, attendance corrections and more wait for you in Approvals. The number on the menu shows how many are pending.', link: { to: '/approvals', label: 'Open approvals' } }] : []),
    { icon: CircleUser, title: 'Your own space', body: 'Payslips, leave, attendance, tax and documents live under My space. Keep your profile current so HR has the right details.', link: { to: '/ess/profile', label: 'Open my profile' } },
    { icon: ShieldCheck, title: 'Keep your account safe', body: 'Turn on two-step verification and review where you are signed in under My profile → Security.', link: { to: '/ess/profile?tab=security', label: 'Security settings' } },
    { icon: Star, title: 'Make it yours', body: 'Star pages you use often, and choose light or dark mode and a compact layout under My profile → Preferences.' },
  ]
}

export function WelcomeTour({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth()
  const { update } = usePreferences()
  const [i, setI] = useState(0)
  const steps = stepsFor(user?.role)
  const step = steps[Math.min(i, steps.length - 1)]
  const last = i >= steps.length - 1

  const finish = async () => {
    // Cosmetic: if the server cannot store it the preference layer keeps it on this device, so
    // the tour still never comes back — no error for the user either way.
    try { await update({ welcome_done: true }) } catch (e) { console.warn('[welcome] could not record tour completion', e) }
    setI(0)
    onClose()
  }

  return (
    <Modal open={open} onClose={() => void finish()} size="sm" title={<span data-testid="welcome-tour">{step.title}</span>}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground num">{i + 1} of {steps.length}</span>
          <div className="flex gap-2">
            {i > 0 && <Button variant="ghost" onClick={() => setI(i - 1)}>Back</Button>}
            {!last && <Button variant="outline" onClick={() => void finish()}>Skip</Button>}
            {last ? <Button onClick={() => void finish()}>Get started</Button> : <Button onClick={() => setI(i + 1)}>Next</Button>}
          </div>
        </div>
      }>
      <div className="flex gap-4">
        <div className="grid size-10 shrink-0 place-items-center rounded-full bg-interactive-subtle text-link"><step.icon className="size-5" /></div>
        <div className="space-y-3">
          <p className="text-sm text-content-secondary">{step.body}</p>
          {step.link && <Link to={step.link.to} onClick={() => void finish()} className="link text-sm">{step.link.label}</Link>}
        </div>
      </div>
      <div className="mt-5 flex justify-center gap-1.5" aria-hidden>
        {steps.map((_, k) => <span key={k} className={k === i ? 'h-1.5 w-4 rounded-full bg-interactive' : 'size-1.5 rounded-full bg-muted'} />)}
      </div>
    </Modal>
  )
}

export function WhatsNewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { prefs, update } = usePreferences()
  const close = () => {
    if (LATEST_RELEASE && prefs.whats_new_seen !== LATEST_RELEASE) void update({ whats_new_seen: LATEST_RELEASE }).catch(() => { /* re-shown next time */ })
    onClose()
  }
  return (
    <Modal open={open} onClose={close} title="What's new" size="md" footer={<Button onClick={close}>Got it</Button>}>
      <div className="space-y-5" data-testid="whats-new">
        {RELEASES.map((r) => (
          <section key={r.version}>
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">{r.title}</h3>
              <span className="text-xs text-muted-foreground">{formatDate(r.date)}</span>
            </div>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-content-secondary">
              {r.items.map((it) => <li key={it}>{it}</li>)}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  )
}

/** True when there is a release the user has not opened yet. */
export function useHasUnseenNews(): boolean {
  const { prefs } = usePreferences()
  return !!LATEST_RELEASE && prefs.whats_new_seen !== LATEST_RELEASE
}
